import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useQueryClient } from 'react-query';

import closeIcon from '../../assets/images/closeIcon.svg';
import FilledButton from '../../components/Buttons/FilledButton';
import FolderTreeSelector from '../../components/FolderTreeSelector';
import Tooltip from '../../components/Tooltip';
import { SocketContext } from '../../context';
import { UiContext } from '../../context/UiContext';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import ImportBulkControls from './ImportBulkControls';
import ImportFileRow from './ImportFileRow';
import runImportQueue from './importPipeline';
import classes from './styles.module.scss';
import {
  validateImportRow,
  MAX_UPLOAD_SIZE_BYTES,
  formatFileSize,
  isImportAllEnabled,
} from './validation';

function createRowFromFile(file, defaultFolder) {
  const { validationError, validationWarning } = validateImportRow(
    file,
    MAX_UPLOAD_SIZE_BYTES
  );
  return {
    rowId: crypto.randomUUID(),
    file,
    title: file.name.replace(/\.[^/.]+$/, '').trim() || 'Untitled video',
    folderId: defaultFolder?.id || null,
    folderName: defaultFolder?.name || null,
    publishNow: true,
    status: 'ready',
    progress: 0,
    validationError,
    validationWarning,
    errorMessage: null,
    meetingId: null,
    sectionId: null,
    processingStep: null,
  };
}

function importReducer(state, action) {
  switch (action.type) {
    case 'INIT_ROWS': {
      const rows = Array.from(action.files).map((file) =>
        createRowFromFile(file, action.defaultFolder)
      );
      return { ...state, rows, isImporting: false, isAllTerminal: false };
    }
    case 'ADD_FILES': {
      const newRows = Array.from(action.files).map((file) =>
        createRowFromFile(file, action.defaultFolder)
      );
      return { ...state, rows: [...state.rows, ...newRows] };
    }
    case 'REMOVE_ROW': {
      const rows = state.rows.filter((r) => r.rowId !== action.rowId);
      return { ...state, rows };
    }
    case 'UPDATE_ROW': {
      const rows = state.rows.map((r) =>
        r.rowId === action.rowId ? { ...r, ...action.updates } : r
      );
      return { ...state, rows };
    }
    case 'SET_ALL_ROWS': {
      const rows = state.rows.map((r) => {
        if (r.status !== 'ready' || r.validationError) return r;
        return { ...r, ...action.updates };
      });
      return { ...state, rows };
    }
    case 'START_IMPORT': {
      return { ...state, isImporting: true };
    }
    case 'CHECK_TERMINAL': {
      const terminal = ['done', 'draft', 'failed'];
      const isAllTerminal =
        state.rows.length > 0 && state.rows.every((r) => terminal.includes(r.status));
      return { ...state, isAllTerminal };
    }
    default:
      return state;
  }
}

const initialState = {
  rows: [],
  isImporting: false,
  isAllTerminal: false,
};

// folderPickerTarget: null, 'bulk', or a rowId string
export default function ImportVideosModal({
  show,
  onHide,
  files,
  defaultFolder,
  fileInputRef,
}) {
  const isMobile = useCheckMobileScreen();
  const queryClient = useQueryClient();
  const { showConfirmActionModal } = useContext(UiContext);
  const [state, dispatch] = useReducer(importReducer, initialState);
  const [folderPickerTarget, setFolderPickerTarget] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const processedFilesRef = useRef(new Set());
  const initializedRef = useRef(false);

  const isBulkFolderPickerVisible = folderPickerTarget === 'bulk';
  const activeRowFolderPickerId =
    folderPickerTarget && folderPickerTarget !== 'bulk'
      ? folderPickerTarget
      : null;

  useEffect(() => {
    if (!files || files.length === 0) return;

    if (!initializedRef.current) {
      dispatch({ type: 'INIT_ROWS', files, defaultFolder });
      Array.from(files).forEach((f) => processedFilesRef.current.add(f));
      initializedRef.current = true;
      return;
    }

    const newFiles = Array.from(files).filter(
      (f) => !processedFilesRef.current.has(f)
    );
    if (newFiles.length > 0) {
      dispatch({ type: 'ADD_FILES', files: newFiles, defaultFolder });
      newFiles.forEach((f) => processedFilesRef.current.add(f));
    }
  }, [files, defaultFolder]);

  useEffect(() => {
    if (!show) {
      initializedRef.current = false;
      processedFilesRef.current = new Set();
      setFolderPickerTarget(null);
      setSelectedFolderId(null);
    }
  }, [show]);

  useEffect(() => {
    if (show && isMobile) {
      onHide();
    }
  }, [isMobile, onHide, show]);

  // Socket listeners for processing events
  const { socket } = useContext(SocketContext);
  const rowsRef = useRef(state.rows);
  useEffect(() => {
    rowsRef.current = state.rows;
  }, [state.rows]);

  useEffect(() => {
    if (!socket || !state.isImporting) return undefined;

    const findProcessingRow = (meetingId) =>
      rowsRef.current.find(
        (r) => r.meetingId === meetingId && r.status === 'processing'
      );

    const onReady = ({ meetingId }) => {
      if (findProcessingRow(meetingId)) {
        dispatch({ type: 'UPDATE_ROW', rowId: findProcessingRow(meetingId).rowId, updates: { status: 'done' } });
        dispatch({ type: 'CHECK_TERMINAL' });
      }
    };

    const onFailed = ({ meetingId }) => {
      if (findProcessingRow(meetingId)) {
        dispatch({
          type: 'UPDATE_ROW',
          rowId: findProcessingRow(meetingId).rowId,
          updates: { status: 'failed', errorMessage: 'Processing failed' },
        });
        dispatch({ type: 'CHECK_TERMINAL' });
      }
    };

    const onProcessing = ({ meetingId, step }) => {
      const row = findProcessingRow(meetingId);
      if (row) {
        dispatch({
          type: 'UPDATE_ROW',
          rowId: row.rowId,
          updates: { processingStep: step },
        });
      }
    };

    socket.on('processingReady', onReady);
    socket.on('publishFailed', onFailed);
    socket.on('processing', onProcessing);

    return () => {
      socket.removeListener('processingReady', onReady);
      socket.removeListener('publishFailed', onFailed);
      socket.removeListener('processing', onProcessing);
    };
  }, [socket, state.isImporting]);

  const resetFolderPickerState = useCallback(() => {
    setFolderPickerTarget(null);
    setSelectedFolderId(null);
  }, []);

  const handleRemoveRow = useCallback((rowId) => {
    if (folderPickerTarget === rowId) {
      resetFolderPickerState();
    }
    dispatch({ type: 'REMOVE_ROW', rowId });
  }, [folderPickerTarget, resetFolderPickerState]);

  const handleUpdateRow = useCallback((rowId, updates) => {
    dispatch({ type: 'UPDATE_ROW', rowId, updates });
  }, []);

  const handleAddMoreFiles = useCallback(() => {
    fileInputRef?.current?.click();
  }, [fileInputRef]);

  const handleOpenFolderPicker = useCallback((target) => {
    if (folderPickerTarget === target) {
      resetFolderPickerState();
      return;
    }

    if (target === 'bulk') {
      const readyRows = state.rows.filter(
        (row) => row.status === 'ready' && !row.validationError
      );
      const firstFolderId = readyRows[0]?.folderId || null;
      const hasSharedFolder =
        !!firstFolderId &&
        readyRows.every((row) => row.folderId === firstFolderId);

      setSelectedFolderId(hasSharedFolder ? firstFolderId : null);
    } else {
      const row = state.rows.find((item) => item.rowId === target);
      setSelectedFolderId(row?.folderId || null);
    }

    setFolderPickerTarget(target);
  }, [folderPickerTarget, resetFolderPickerState, state.rows]);

  const handleFolderSelect = useCallback((folderId, folderName) => {
    if (!folderId) return;
    if (folderPickerTarget === 'bulk') {
      dispatch({
        type: 'SET_ALL_ROWS',
        updates: { folderId, folderName: folderName || null },
      });
    } else {
      dispatch({
        type: 'UPDATE_ROW',
        rowId: folderPickerTarget,
        updates: { folderId, folderName: folderName || null },
      });
    }

    resetFolderPickerState();
  }, [folderPickerTarget, resetFolderPickerState]);

  const handleImportAll = useCallback(() => {
    resetFolderPickerState();
    dispatch({ type: 'START_IMPORT' });

    const onRowUpdate = (rowId, updates) => {
      dispatch({ type: 'UPDATE_ROW', rowId, updates });
      const terminalStatuses = ['done', 'draft', 'failed'];
      if (updates.status && terminalStatuses.includes(updates.status)) {
        dispatch({ type: 'CHECK_TERMINAL' });
      }
    };

    runImportQueue(state.rows, onRowUpdate);
  }, [resetFolderPickerState, state.rows]);

  // beforeunload: warn when leaving during active import
  useEffect(() => {
    if (!state.isImporting || state.isAllTerminal) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'Import is in progress. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.isImporting, state.isAllTerminal]);

  const handleClose = useCallback(() => {
    if (state.isImporting && !state.isAllTerminal) {
      showConfirmActionModal({
        message:
          'Import is in progress. Videos that have already been created will remain in your library. Are you sure you want to cancel?',
        onConfirm: () => {
          queryClient.invalidateQueries(['meetings']);
          queryClient.invalidateQueries(['folders']);
          onHide();
        },
      });
      return;
    }
    if (state.isAllTerminal) {
      queryClient.invalidateQueries(['meetings']);
      queryClient.invalidateQueries(['folders']);
    }
    onHide();
  }, [state.isImporting, state.isAllTerminal, showConfirmActionModal, queryClient, onHide]);

  const { rows, isImporting, isAllTerminal } = state;
  const canImport = isImportAllEnabled(rows);
  const importDisabled = !canImport || isImporting;

  const summaryBanner = useMemo(() => {
    if (!isAllTerminal || rows.length === 0) return null;
    const doneCount = rows.filter((r) => r.status === 'done').length;
    const draftCount = rows.filter((r) => r.status === 'draft').length;
    const failedCount = rows.filter((r) => r.status === 'failed').length;

    if (failedCount === rows.length) return { type: 'error', text: `All ${rows.length} imports failed` };
    if (doneCount === rows.length) return { type: 'success', text: `All ${rows.length} videos imported successfully` };
    if (draftCount === rows.length) return { type: 'success', text: `All ${rows.length} videos saved as drafts` };

    const parts = [];
    if (doneCount > 0) parts.push(`${doneCount} published`);
    if (draftCount > 0) parts.push(`${draftCount} saved as draft`);
    if (failedCount > 0) parts.push(`${failedCount} failed`);
    return { type: failedCount > 0 ? 'warning' : 'success', text: parts.join(', ') };
  }, [isAllTerminal, rows]);

  const importTooltip = (() => {
    if (isImporting) return null;
    if (rows.length === 0) return null;
    const importable = rows.filter((r) => !r.validationError);
    if (importable.length === 0) return 'No valid files to import';
    if (!importable.every((r) => r.folderId)) return 'Please assign a folder to all videos';
    return null;
  })();

  if (isMobile) {
    return null;
  }

  return (
    <Modal
      show={show}
      centered
      size="lg"
      contentClassName={classes.ImportVideosModal}
      dialogClassName={classes.dialog}
      backdropClassName={classes.backdrop}
      onHide={handleClose}
    >
      <i
        className={classes.closeIcon}
        onClick={handleClose}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClose();
          }
        }}
      >
        <img src={closeIcon} alt="Close" />
      </i>

      <div className={classes.content}>
        <h2>Import Existing Videos</h2>

        {summaryBanner && (
          <div className={`${classes.summaryBanner} ${classes[`summary_${summaryBanner.type}`]}`}>
            {summaryBanner.text}
          </div>
        )}

        {rows.length > 0 && !isImporting && !isAllTerminal && (
          <ImportBulkControls
            rows={rows}
            isImporting={isImporting}
            onPublishAll={() =>
              dispatch({ type: 'SET_ALL_ROWS', updates: { publishNow: true } })
            }
            onUnpublishAll={() =>
              dispatch({ type: 'SET_ALL_ROWS', updates: { publishNow: false } })
            }
            onOpenFolderPicker={() => handleOpenFolderPicker('bulk')}
            folderTreeContent={
              isBulkFolderPickerVisible ? (
                <div className={classes.bulkFolderTree}>
                  <FolderTreeSelector
                    showCheckboxes={false}
                    showMeetings={false}
                    showRoot={false}
                    selectedFolderId={selectedFolderId}
                    onFolderSelect={handleFolderSelect}
                    variant="compact"
                  />
                </div>
              ) : null
            }
          />
        )}

        {rows.length === 0 ? (
          <div className={classes.emptyState}>
            <p>No files selected</p>
            <div className={classes.emptyActions}>
              <button
                type="button"
                className={classes.addMoreButton}
                onClick={handleAddMoreFiles}
              >
                + Select files
              </button>
              <button
                type="button"
                className={classes.closeButton}
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={classes.tableWrapper}>
              <table className={classes.fileTable}>
                <thead>
                  <tr>
                    {!isImporting && <th className={classes.colRemove} />}
                    <th className={classes.colTitle}>Title</th>
                    <th className={classes.colSize}>Size</th>
                    <th className={classes.colFolder}>Folder</th>
                    <th className={classes.colPublish}>Publish now</th>
                    <th className={classes.colStatus}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <ImportFileRow
                      key={row.rowId}
                      row={row}
                      isImporting={isImporting}
                      onRemove={handleRemoveRow}
                      onUpdate={handleUpdateRow}
                      onOpenFolderPicker={handleOpenFolderPicker}
                      isFolderPickerOpen={
                        activeRowFolderPickerId === row.rowId
                      }
                      selectedFolderId={selectedFolderId}
                      onFolderSelect={handleFolderSelect}
                      formatFileSize={formatFileSize}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {!isImporting && !isAllTerminal && (
              <button
                type="button"
                className={classes.addMoreButton}
                onClick={handleAddMoreFiles}
              >
                + Add more files
              </button>
            )}
          </>
        )}

        <div className={classes.footer}>
          {isAllTerminal ? (
            <FilledButton onClick={handleClose} width={160}>
              Done
            </FilledButton>
          ) : (
            <>
              <button
                type="button"
                className={classes.cancelButton}
                onClick={handleClose}
              >
                Cancel
              </button>
              {importTooltip ? (
                <Tooltip text={importTooltip} position="left" width={220}>
                  <FilledButton
                    onClick={handleImportAll}
                    isDisabled={importDisabled}
                    width={160}
                  >
                    Import all
                  </FilledButton>
                </Tooltip>
              ) : (
                <FilledButton
                  onClick={handleImportAll}
                  isDisabled={importDisabled}
                  width={160}
                >
                  Import all
                </FilledButton>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
